import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FilePlus, Camera, CheckCircle2, AlertTriangle, Loader2, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { btn, btnPrimary, selectCls, labelCls, errMsg, fmtBytes } from "@/components/accounting/ui";
import CameraCapture from "@/components/accounting/CameraCapture";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif";

const QueueItem = ({ it }) => (
  <div className="flex items-center gap-3 py-2 border-b border-line/70 last:border-0" data-testid="acc-upload-item">
    <span className="shrink-0">
      {it.status === "ok" && (it.duplicate ? <Copy size={15} className="text-amber-600" /> : <CheckCircle2 size={15} className="text-emerald-600" />)}
      {it.status === "err" && <AlertTriangle size={15} className="text-red-600" />}
      {(it.status === "up" || it.status === "wait") && <Loader2 size={15} className="text-faint animate-spin" />}
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-ink truncate">{it.name} <span className="text-faint">· {fmtBytes(it.size)}</span></p>
      {it.status === "up" && <div className="h-1 rounded-full bg-elevated mt-1.5 overflow-hidden"><div className="h-full bg-brand transition-[width] duration-200" style={{ width: `${it.progress}%` }} /></div>}
      {it.status === "err" && <p className="text-[11px] text-red-600 mt-0.5" data-testid="acc-upload-item-error">{it.error}</p>}
      {it.duplicate && <p className="text-[11px] text-amber-700 mt-0.5">Gespeichert – identische Datei existiert bereits (Duplikat)</p>}
    </div>
  </div>
);

export const UploadPanel = ({ period, label, meta, onUploaded }) => {
  const [category, setCategory] = useState("einkauf");
  const [docType, setDocType] = useState("");
  const [queue, setQueue] = useState([]);
  const [drag, setDrag] = useState(false);
  const [capture, setCapture] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const types = meta.categories.find((c) => c.id === category).types;
  const maxBytes = meta.limits.max_mb * 1048576;

  const uploadOne = async (file, opts, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", opts.category);
    fd.append("period", period);
    fd.append("doc_type", opts.docType || "");
    fd.append("source", opts.source);
    const { data } = await api.post("/admin/accounting/docs", fd, {
      onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
    });
    return data;
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const stamp = Date.now();
    const items = files.map((f, i) => ({ key: `${stamp}-${i}`, name: f.name, size: f.size, progress: 0, status: "wait" }));
    setQueue((q) => [...items, ...q].slice(0, 40));
    let ok = 0; let dup = 0; let failed = 0;
    for (let i = 0; i < files.length; i += 1) {
      const key = items[i].key;
      const patch = (p) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...p } : x)));
      if (files[i].size > maxBytes) { failed += 1; patch({ status: "err", error: `Größer als ${meta.limits.max_mb} MB` }); continue; }
      patch({ status: "up" });
      try {
        const res = await uploadOne(files[i], { category, docType, source: "upload" }, (p) => patch({ progress: p }));
        ok += 1;
        if (res.duplicates.length) dup += 1;
        patch({ status: "ok", progress: 100, duplicate: res.duplicates.length > 0 });
      } catch (e) {
        failed += 1;
        patch({ status: "err", error: errMsg(e, "Upload fehlgeschlagen") });
      }
    }
    if (ok) toast.success(`${ok} ${ok === 1 ? "Beleg" : "Belege"} gespeichert`);
    if (dup) toast.warning(`${dup} ${dup === 1 ? "mögliches Duplikat" : "mögliche Duplikate"} erkannt`);
    if (failed) toast.error(`${failed} ${failed === 1 ? "Datei wurde" : "Dateien wurden"} nicht gespeichert`);
    onUploaded();
  };

  const saveCapture = async (opts) => {
    try {
      const res = await uploadOne(capture, { ...opts, source: "camera" });
      toast.success(res.duplicates.length ? "Foto gespeichert – mögliches Duplikat erkannt" : "Foto als Beleg gespeichert");
      setCapture(null);
      onUploaded();
    } catch (e) {
      toast.error(errMsg(e, "Foto konnte nicht gespeichert werden"));
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); };

  return (
    <div className="rounded-xl border border-line bg-surface p-5 md:p-6" data-testid="acc-upload-panel">
      <p className={labelCls}>Belege hinzufügen · {label}</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <select value={category} onChange={(e) => { setCategory(e.target.value); setDocType(""); }} className={selectCls} data-testid="acc-upload-category" aria-label="Kategorie">
          {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className={selectCls} data-testid="acc-upload-doctype" aria-label="Belegart">
          <option value="">Belegart später festlegen</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      <div data-testid="acc-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
        className={`mt-4 rounded-xl border-2 border-dashed px-5 py-8 text-center transition-colors ${drag ? "border-brand bg-brand/5" : "border-line"}`}>
        <UploadCloud size={26} strokeWidth={1.5} className="mx-auto text-faint" />
        <p className="text-sm text-ink font-medium mt-2 hidden md:block">Dateien hierher ziehen</p>
        <p className="text-xs text-faint mt-1">{meta.limits.formats.join(", ")} · max. {meta.limits.max_mb} MB pro Datei · mehrere gleichzeitig</p>
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          <button type="button" onClick={() => fileRef.current.click()} className={btnPrimary} data-testid="acc-select-files-btn"><FilePlus size={15} /> Dateien auswählen</button>
          <button type="button" onClick={() => camRef.current.click()} className={`${btn} px-5 py-2.5 text-sm`} data-testid="acc-camera-btn"><Camera size={15} /> Beleg fotografieren</button>
        </div>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="acc-file-input"
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="acc-camera-input"
          onChange={(e) => { setCapture(e.target.files[0] || null); e.target.value = ""; }} />
      </div>
      {queue.length > 0 && (
        <div className="mt-4" data-testid="acc-upload-queue">
          {queue.map((it) => <QueueItem key={it.key} it={it} />)}
          <button type="button" onClick={() => setQueue((q) => q.filter((x) => x.status === "up" || x.status === "wait"))}
            className="text-[11px] text-faint hover:text-ink mt-2" data-testid="acc-upload-queue-clear">Liste leeren</button>
        </div>
      )}
      <CameraCapture file={capture} meta={meta} defaultCategory={category} label={label} onSave={saveCapture}
        onClose={() => setCapture(null)} onRetake={() => camRef.current.click()} />
    </div>
  );
};

export default UploadPanel;
